import { type HierarchyManager } from '@utazzo/shared';
import { UzDialog } from '@utazzo/ui';
import { ChevronRight, Crown, User as UserIcon, Users } from 'lucide-react';
import React from 'react';

import { useUserHierarchy } from '../use-users';

interface UserHierarchyDialogProps {
  open: boolean;
  userId: string | null;
  onOpenChange: (open: boolean) => void;
}

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrator',
  SALES_LEAD: 'Sales Lead',
  SALES_AGENT: 'Sales Agent',
  OPS: 'Operations',
  FINANCE: 'Finance',
  HR: 'HR',
  READ_ONLY: 'Read-only',
  CONTENT_EDITOR: 'Content Editor',
  MARKETING_LEAD: 'Marketing Lead',
};

const roleLabel = (role: string): string => ROLE_LABELS[role] ?? role;

interface NodeRowProps {
  fullName: string;
  email: string;
  role: string;
  branch?: { id: string; name: string } | null;
  department?: { id: string; name: string } | null;
  isActive?: boolean;
  highlight?: boolean;
  icon?: React.ReactNode;
}

const NodeRow = ({
  fullName,
  email,
  role,
  branch,
  department,
  isActive = true,
  highlight = false,
  icon,
}: NodeRowProps): React.JSX.Element => (
  <div
    className={`flex items-start gap-2 rounded-md border px-3 py-2 ${
      highlight ? 'border-violet-200 bg-violet-50' : 'border-zinc-200 bg-white'
    }`}
  >
    <span className="mt-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-500">
      {icon ?? <UserIcon className="h-4 w-4" />}
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate font-medium text-zinc-900">{fullName}</span>
        <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-zinc-700 uppercase">
          {roleLabel(role)}
        </span>
        {!isActive && (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-amber-700 uppercase">
            inactive
          </span>
        )}
      </div>
      <div className="truncate text-xs text-zinc-500">{email}</div>
      {(branch || department) && (
        <div className="mt-0.5 truncate text-[11px] text-zinc-400">
          {branch?.name}
          {branch && department && ' · '}
          {department?.name}
        </div>
      )}
    </div>
  </div>
);

/// Renders a manager chain from immediate parent up to the root admin.
/// Reverses the array so the topmost manager (root) appears first.
const ManagerChain = ({ managers }: { managers: HierarchyManager[] }): React.JSX.Element | null => {
  if (managers.length === 0) {
    return (
      <div className="text-sm text-zinc-500">
        This user reports to no one (root role or unassigned).
      </div>
    );
  }
  // Reverse so root admin appears at the top and the immediate manager
  // sits just above the subject - matches a standard org-chart view.
  const ordered = [...managers].reverse();
  return (
    <div className="flex flex-col gap-2">
      {ordered.map((m, i) => (
        <div
          key={m.id}
          className="flex flex-col items-start gap-1"
          style={{ marginLeft: `${i * 16}px` }}
        >
          <NodeRow
            fullName={m.fullName}
            email={m.email}
            role={m.role}
            icon={i === 0 ? <Crown className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
          />
          <ChevronRight className="ml-3 h-3 w-3 rotate-90 text-zinc-400" />
        </div>
      ))}
    </div>
  );
};

/// Renders a tree of direct reports. The two concrete `reports` types
/// returned by the API (mid-level + leaf) have identical shape modulo
/// the recursive `reports` array, so we accept a structural slice and
/// keep the recursion generic.
interface ReportLike {
  id: string;
  fullName: string;
  email: string;
  role: string;
  branch: { id: string; name: string } | null;
  department: { id: string; name: string } | null;
  isActive: boolean;
  reports: ReadonlyArray<ReportLike>;
}
interface ReportsTreeProps {
  reports: ReadonlyArray<ReportLike>;
  depth?: number;
}
const ReportsTree = ({ reports, depth = 0 }: ReportsTreeProps): React.JSX.Element | null => {
  if (reports.length === 0) return null;
  return (
    <div className="flex flex-col gap-2" style={{ marginLeft: depth === 0 ? 0 : 16 }}>
      {reports.map((r) => (
        <div key={r.id} className="flex flex-col gap-2">
          <NodeRow
            fullName={r.fullName}
            email={r.email}
            role={r.role}
            branch={r.branch}
            department={r.department}
            isActive={r.isActive}
          />
          {r.reports.length > 0 && <ReportsTree reports={r.reports} depth={depth + 1} />}
        </div>
      ))}
    </div>
  );
};

export const UserHierarchyDialog = ({
  open,
  userId,
  onOpenChange,
}: UserHierarchyDialogProps): React.JSX.Element => {
  const query = useUserHierarchy(open ? userId : null);

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Organization chart"
      description="Reporting chain up to the root + direct reports two levels down."
      size="xl"
    >
      {query.isLoading && <div className="p-4 text-sm text-zinc-500">Loading hierarchy…</div>}
      {query.error && <div className="p-4 text-sm text-red-600">Could not load the hierarchy.</div>}
      {query.data && (
        <div className="flex flex-col gap-5">
          <section>
            <div className="mb-2 text-[11px] font-medium tracking-wider text-zinc-500 uppercase">
              Manager chain
            </div>
            <ManagerChain managers={query.data.managers} />
          </section>

          <section>
            <div className="mb-2 text-[11px] font-medium tracking-wider text-zinc-500 uppercase">
              This user
            </div>
            <NodeRow
              fullName={query.data.user.fullName}
              email={query.data.user.email}
              role={query.data.user.role}
              branch={query.data.user.branch}
              department={query.data.user.department}
              isActive={query.data.user.isActive}
              highlight
            />
          </section>

          <section>
            <div className="mb-2 flex items-center gap-1 text-[11px] font-medium tracking-wider text-zinc-500 uppercase">
              <Users className="h-3.5 w-3.5" />
              Direct reports ({query.data.user.reports.length})
            </div>
            {query.data.user.reports.length === 0 ? (
              <div className="text-sm text-zinc-500">No direct reports.</div>
            ) : (
              <ReportsTree reports={query.data.user.reports} />
            )}
          </section>
        </div>
      )}
    </UzDialog>
  );
};
